import { useMemo } from 'react'
import GoogleMap, { MapContextProps } from 'google-maps-react-markers'

import Container from '@/components/base/Container'
import SVGIcon from '@/components/base/SVGIcon'

import { useStore } from '@/contexts/store'
import { Section, WPImage } from '@/types'

const mapStyles = [
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e9e9e9'
      },
      {
        lightness: 17
      }
    ]
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5'
      },
      {
        lightness: 20
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff'
      },
      {
        lightness: 17
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#ffffff'
      },
      {
        lightness: 29
      },
      {
        weight: 0.2
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff'
      },
      {
        lightness: 18
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff'
      },
      {
        lightness: 16
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5'
      },
      {
        lightness: 21
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      {
        color: '#dedede'
      },
      {
        lightness: 21
      }
    ]
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [
      {
        visibility: 'on'
      },
      {
        color: '#ffffff'
      },
      {
        lightness: 16
      }
    ]
  },
  {
    elementType: 'labels.text.fill',
    stylers: [
      {
        saturation: 36
      },
      {
        color: '#333333'
      },
      {
        lightness: 40
      }
    ]
  },
  {
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [
      {
        color: '#f2f2f2'
      },
      {
        lightness: 19
      }
    ]
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#fefefe'
      },
      {
        lightness: 20
      }
    ]
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#fefefe'
      },
      {
        lightness: 17
      },
      {
        weight: 1.2
      }
    ]
  }
]

type MarkerProps = Partial<{
  lat: string
  lng: string
}>

// eslint-disable-next-line no-unused-vars
const Marker = (props: MarkerProps) => {
  return (
    <SVGIcon
      symbol="map-marker"
      className="absolute left-1/2 top-1/2 h-[2.4rem] w-[1.9rem] -translate-x-1/2 -translate-y-1/2 fill-blue-soft"
    />
  )
}

type MapProps = Section &
  Partial<{
    placeholder: WPImage
    markers: {
      marker: {
        lat: string
        lng: string
      }
    }[]
  }>

const Map = ({ markers = [], ...props }: MapProps) => {
  const { strings } = useStore()

  const defaultCenter = useMemo(
    () => ({ lat: 41.8902764, lng: 12.4923703 }),
    []
  )

  const onGoogleApiLoaded = ({
    map,
    maps
  }: {
    map: MapContextProps['map']
    maps: MapContextProps['maps']
  }) => {}

  return markers.length > 0 ? (
    <section {...props}>
      <Container className="relative">
        <div className="aspect-h-[620] aspect-w-[360] md:aspect-h-[620] md:aspect-w-[840] lg:aspect-h-[620] lg:aspect-w-[1240]">
          <div className="absolute left-0 top-0 h-full w-full overflow-hidden shadow-3xl md:rounded-xxs">
            <GoogleMap
              apiKey={
                strings?.google_map_api_key ||
                process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY
              }
              defaultZoom={5.7}
              defaultCenter={defaultCenter}
              onGoogleApiLoaded={onGoogleApiLoaded}
              options={{
                panControl: true,
                gestureHandling: 'cooperative',
                clickableIcons: false,
                scrollwheel: false,
                styles: mapStyles,
                streetViewControl: false,
                mapTypeControl: false
              }}
            >
              {markers
                ?.filter(
                  ({ marker }) => marker?.lat !== '' || marker?.lng !== ''
                )
                .map(({ marker }, index) => {
                  return (
                    <Marker
                      key={`marker-${index}`}
                      lat={marker?.lat}
                      lng={marker?.lng}
                    />
                  )
                })}
            </GoogleMap>
          </div>
        </div>
      </Container>
    </section>
  ) : null
}

export default Map
